import {useMemo} from "react";
import {useRoomConfig} from "../RoomConfigContext";
import {useScreenConfig} from "../ScreenConfigContext";
import {createScore, updateScore, pushError} from "../utils/score";
import {ErrorCodes} from "../ErrorCodes";
import getCeilingHeight from "../utils/getCeilingHeight";

// ceiling speakers don't use the regular constraint / validation ruleset. Instead, their positions are entirely
// calculated before validation
export const useSeatValidator = (numRows, numSeats, distance, primarySeatDistance, config) => {
    const {freeWidth, freeDepth, freeHeight, roofHeight} = useRoomConfig();
    const {screenSize} = useScreenConfig();

    return useMemo(() => {
        const d = distance + (numRows - 1) * config.rowDepth;
        const w = numSeats * config.size[0];
        const optimalDistance = screenSize * 1.5;
        const optimalTolerance = 100;
        const score = createScore();

        if (d > freeDepth - config.recline + 0.01 || d < config.rowDepth - 0.01 || w > freeWidth)
            updateScore(score, 3, ErrorCodes.SeatsTooBigForRoom);

        const diff = primarySeatDistance - optimalDistance;

        if (diff > optimalTolerance)
            updateScore(score, 2, ErrorCodes.SeatsTooFar);
        else if (diff < -optimalTolerance)
            updateScore(score, 2, ErrorCodes.SeatsTooNear);
        else if (diff > optimalTolerance * .5)
            updateScore(score, 1, ErrorCodes.SeatsTooFar);
        else if (diff < -optimalTolerance * .5)
            updateScore(score, 1, ErrorCodes.SeatsTooNear);

        // allow for 1m20 above the row
        const maxX = config.size[0] * numSeats / 2;
        // calculate where the roof is
        const ceiling = getCeilingHeight(maxX, roofHeight, freeWidth, freeHeight);
        const maxY = (numRows - 1) * config.rowHeight + 120;

        if (maxY > ceiling)
            updateScore(score, 3, ErrorCodes.SeatsTooHighForCeiling);

        if (numRows > 1) {
            pushError(score, ErrorCodes.MultipleSeatingRows);
        }

        return score;
    }, [freeHeight, roofHeight, freeDepth, freeWidth, config, numRows, numSeats, distance, primarySeatDistance, screenSize]);
}
